import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { useLocation } from "react-router-dom";
import { Button } from "reactstrap";
import {
  useContestMap,
  useMultipleUserSubmissions,
  useProblemMap,
} from "../api/APIClient";
import {
  constructStatusLabelMap,
  noneStatus,
  ProblemId,
  ProblemStatus,
  StatusLabel,
} from "../interfaces/Status";
import { caseInsensitiveUserId } from "../utils";
import { useLocalStorage } from "../utils/LocalStorage";
import { ProblemLink } from "./ProblemLink";

const extractUserIdFromHash = (pathname: string): string | undefined => {
  const match = /^\/(?:table|list|user)\/([a-zA-Z0-9_]+)/.exec(pathname);
  return match ? caseInsensitiveUserId(match[1]) : undefined;
};

const extractProblemId = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = /atcoder\.jp\/contests\/[^/]+\/tasks\/([a-zA-Z0-9_]+)/.exec(
    trimmed
  );
  return match ? match[1] : null;
};

// Map a status label to a card background color matching the table colors.
// Uses Bootstrap's table-* CSS variables so it tracks the theme.
const statusToCardBackground = (label: StatusLabel): string => {
  switch (label) {
    case StatusLabel.Success:
      return "rgba(195, 230, 203, 0.55)"; // table-success
    case StatusLabel.Warning:
      return "rgba(255, 238, 186, 0.55)"; // table-warning
    case StatusLabel.None:
    default:
      return "rgba(0,0,0,0.02)";
  }
};

const PANEL_WIDTH = 380;

export const TodoDrawer: React.FC = () => {
  const [enabled, setEnabled] = useLocalStorage<boolean>("todoEnabled", false);
  const [open, setOpen] = useState(false);
  const [todoIds, setTodoIds] = useLocalStorage<string[]>("todoProblems", []);
  const problemMap = useProblemMap();
  const contestMap = useContestMap();

  const { pathname } = useLocation();
  const userId = extractUserIdFromHash(pathname);

  const submissionsData = useMultipleUserSubmissions(userId ? [userId] : [])
    .data;

  const statusLabelMap = useMemo<Map<ProblemId, ProblemStatus>>(
    () =>
      userId
        ? constructStatusLabelMap(submissionsData ?? [], userId)
        : new Map<ProblemId, ProblemStatus>(),
    [submissionsData, userId]
  );

  const [dragOver, setDragOver] = useState(false);
  const [pulse, setPulse] = useState(0);

  const items = useMemo(
    () =>
      todoIds.map((id) => {
        const problem = problemMap?.get(id);
        const status = statusLabelMap.get(id) ?? noneStatus();
        const contest = problem
          ? contestMap?.get(problem.contest_id)
          : undefined;
        return { id, problem, status, contest };
      }),
    [todoIds, problemMap, statusLabelMap, contestMap]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Shift+T to toggle availability of the TODO drawer.
      // Ignore when typing in inputs or when other modifiers are held.
      if (e.key !== "T") return;
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      setEnabled((prev) => !prev);
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setEnabled]);

  if (!enabled) return null;

  const addProblemId = (id: string): string | null => {
    if (todoIds.includes(id)) {
      return `${id} はすでに追加されています`;
    }
    if (problemMap && !problemMap.has(id)) {
      return `${id} に該当する問題が見つかりません`;
    }
    setTodoIds([...todoIds, id]);
    setPulse((n) => n + 1);
    return null;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    const candidates = [
      e.dataTransfer.getData("text/uri-list"),
      e.dataTransfer.getData("text/plain"),
      e.dataTransfer.getData("text"),
    ].filter(Boolean);

    for (const c of candidates) {
      for (const line of c.split(/\r?\n/)) {
        const id = extractProblemId(line);
        if (!id) continue;
        addProblemId(id);
        return;
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget === e.target) setDragOver(false);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const next = todoIds.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setTodoIds(next);
  };

  const remove = (index: number) => {
    setTodoIds(todoIds.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    if (todoIds.length === 0) return;
    if (window.confirm("TODOリストを全て削除しますか？")) {
      setTodoIds([]);
    }
  };

  const toggleWrapperStyle: React.CSSProperties = {
    position: "fixed",
    right: open ? PANEL_WIDTH : 0,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1051,
    transition: "right 0.25s ease",
    outline: dragOver ? "3px dashed #0d6efd" : "3px dashed transparent",
    outlineOffset: "2px",
    borderRadius: "0.5rem 0 0 0.5rem",
  };

  const toggleButtonStyle: React.CSSProperties = {
    writingMode: "vertical-rl",
    padding: "1rem 0.4rem",
    borderTopLeftRadius: "0.5rem",
    borderBottomLeftRadius: "0.5rem",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: "-2px 0 8px rgba(0,0,0,0.15)",
  };

  const panelStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    right: 0,
    width: PANEL_WIDTH,
    height: "100vh",
    background: "var(--bs-body-bg, #f6f7f9)",
    color: "var(--bs-body-color, inherit)",
    boxShadow: "-4px 0 12px rgba(0,0,0,0.2)",
    transform: open ? "translateX(0)" : `translateX(${PANEL_WIDTH}px)`,
    transition: "transform 0.25s ease",
    zIndex: 1050,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    padding: "1rem",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "rgba(0,0,0,0.02)",
  };

  const bodyStyle: React.CSSProperties = {
    padding: "0.75rem",
    overflowY: "auto",
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    padding: "0.5rem 1rem",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(0,0,0,0.02)",
    display: "flex",
    justifyContent: "flex-end",
  };

  const cardStyle = (
    label: StatusLabel,
    isDragging: boolean
  ): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 0.6rem",
    marginBottom: "0.4rem",
    borderRadius: "0.5rem",
    border: "1px solid rgba(0,0,0,0.08)",
    background: statusToCardBackground(label),
    boxShadow: isDragging
      ? "0 6px 16px rgba(0,0,0,0.18)"
      : "0 1px 2px rgba(0,0,0,0.05)",
    transition: "box-shadow 0.15s ease",
  });

  return (
    <>
      <style>{`
        @keyframes todoTogglePulse {
          0%   { box-shadow: -2px 0 8px rgba(0,0,0,0.15), 0 0 0 0 rgba(255,193,7,0.0); }
          30%  { box-shadow: -2px 0 8px rgba(0,0,0,0.15), 0 0 0 10px rgba(255,193,7,0.6); }
          100% { box-shadow: -2px 0 8px rgba(0,0,0,0.15), 0 0 0 24px rgba(255,193,7,0); }
        }
      `}</style>
      <div
        style={toggleWrapperStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        title="ここに問題ページのURLをドラッグ&ドロップで追加"
      >
        <Button
          key={pulse}
          color="primary"
          style={{
            ...toggleButtonStyle,
            animation: pulse > 0 ? "todoTogglePulse 0.6s ease-out" : undefined,
          }}
          onClick={() => setOpen((v) => !v)}
          aria-label="TODOリストを開閉"
        >
          TODO
        </Button>
      </div>

      <div style={panelStyle} role="dialog" aria-hidden={!open}>
        <div style={headerStyle}>
          <h5 className="mb-0">TODO リスト</h5>
          <Button close aria-label="閉じる" onClick={() => setOpen(false)} />
        </div>

        <div style={bodyStyle}>
          {items.length === 0 ? (
            <em className="text-muted">まだ何も登録されていません。</em>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="todo-list">
                {(droppableProvided) => (
                  <div
                    // eslint-disable-next-line @typescript-eslint/unbound-method
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {items.map(({ id, problem, status }, index) => (
                      <Draggable key={id} draggableId={id} index={index}>
                        {(
                          draggableProvided: DraggableProvided,
                          snapshot: DraggableStateSnapshot
                        ) => {
                          const card = (
                            <div
                              // eslint-disable-next-line @typescript-eslint/unbound-method
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              title="ドラッグして並び替え"
                              style={{
                                ...cardStyle(status.label, snapshot.isDragging),
                                cursor: snapshot.isDragging
                                  ? "grabbing"
                                  : "grab",
                                ...draggableProvided.draggableProps.style,
                              }}
                            >
                              <div
                                style={{
                                  cursor: "grab",
                                  color: "rgba(0,0,0,0.35)",
                                  userSelect: "none",
                                  fontSize: "1.1rem",
                                  lineHeight: 1,
                                  padding: "0 0.25rem",
                                }}
                              >
                                ⠿
                              </div>
                              <div
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "auto",
                                }}
                              >
                                {problem ? (
                                  <ProblemLink
                                    problemId={problem.id}
                                    contestId={problem.contest_id}
                                    problemIndex={problem.problem_index}
                                    problemName={problem.name}
                                  />
                                ) : (
                                  <span className="text-muted">{id}</span>
                                )}
                              </div>
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                onClick={() => remove(index)}
                                title="削除"
                                style={{
                                  lineHeight: 1,
                                  padding: "0.15rem 0.4rem",
                                }}
                              >
                                ×
                              </Button>
                            </div>
                          );
                          // While dragging, render through a portal attached to
                          // document.body so the floating clone is not clipped
                          // by the panel's `overflow: hidden`.
                          return snapshot.isDragging
                            ? createPortal(card, document.body)
                            : card;
                        }}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {items.length > 0 && (
          <div style={footerStyle}>
            <Button
              color="link"
              size="sm"
              className="text-danger p-0"
              onClick={clearAll}
            >
              全て削除
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
